import axios from 'axios';
import { AuditLogFilters, AuditLogResponse, ApiResponse } from '../types';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5269/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const auditService = {
  // Get audit logs with filters
  getAuditLogs: async (filters: AuditLogFilters): Promise<ApiResponse<AuditLogResponse>> => {
    const params = new URLSearchParams();

    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.companyId !== undefined) params.append('companyId', filters.companyId.toString());
    if (filters.action) params.append('action', filters.action);
    if (filters.entityType) params.append('entityType', filters.entityType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.logLevel) params.append('logLevel', filters.logLevel);
    if (filters.errorCode) params.append('errorCode', filters.errorCode);
    params.append('pageNumber', filters.pageNumber.toString());
    params.append('pageSize', filters.pageSize.toString());

    const response = await api.get(`/audit-logs?${params.toString()}`);
    return response.data;
  },

  // Export audit logs to CSV
  exportAuditLogs: async (filters: Omit<AuditLogFilters, 'pageNumber' | 'pageSize'>): Promise<Blob> => {
    const params = new URLSearchParams();

    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.companyId !== undefined) params.append('companyId', filters.companyId.toString());
    if (filters.action) params.append('action', filters.action);
    if (filters.entityType) params.append('entityType', filters.entityType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.logLevel) params.append('logLevel', filters.logLevel);
    if (filters.errorCode) params.append('errorCode', filters.errorCode);

    const response = await api.get(`/audit-logs/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get distinct users for filter dropdown
  getDistinctUsers: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/audit-logs/users');
    return response.data;
  },

  // Get distinct actions for filter dropdown
  getDistinctActions: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/audit-logs/actions');
    return response.data;
  },

  // Get distinct entity types for filter dropdown
  getDistinctEntityTypes: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/audit-logs/entity-types');
    return response.data;
  },

  // Get distinct log levels for filter dropdown
  getDistinctLogLevels: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/audit-logs/log-levels');
    return response.data;
  },

  // Get distinct error codes for filter dropdown
  getDistinctErrorCodes: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/audit-logs/error-codes');
    return response.data;
  },

  // Get distinct companies for filter dropdown
  getDistinctCompanies: async (): Promise<ApiResponse<Array<{ id: number; name: string }>>> => {
    const response = await api.get('/audit-logs/companies');
    return response.data;
  },
};
